import { CountryId, DefaultSettings, AddressFieldName, ErrorMessage, } from '@as-react/common-types';
import { capitalizeOnly } from '@as-react/common-utils';
import * as Yup from 'yup';
import { mapWithAssert } from '../../../util/ArrayUtil';
export const emptyDeliveryAddressFields = {
    addToProfile: false,
    address: '',
    addressLine2: '',
    addressLine3: '',
    addressLine4: '',
    city: '',
    country: '',
    county: '',
    defaultAddress: false,
    firstName: '',
    houseNumber: '',
    houseNumberAddition: '',
    isActive: false,
    lastName: '',
    mainAddress: false,
    middleName: '',
    postCode: '',
    state: '',
};
export const emptyAddressFields = {
    address: '',
    addressLine2: '',
    addressLine3: '',
    addressLine4: '',
    city: '',
    cityFrench: undefined,
    country: '',
    county: '',
    houseNumber: '',
    houseNumberAddition: '',
    postCode: '',
};
Yup.addMethod(Yup.string, 'conditionalMatches', function (regex, message) {
    return this.test({
        name: 'conditionalMatches',
        message,
        test: value => (!value ? true : regex.test(value)),
    });
});
export const generateFieldName = (name, prefix) => prefix ? `${prefix}${prefix.endsWith('.') ? name : capitalizeOnly(name)}` : name;
export const getHouseNumberNLPattern = (value) => new RegExp('^[A-Z]{1}', 'i').test(value) ? DefaultSettings.HOUSE_NUMBER_NL_ALPHA : DefaultSettings.HOUSE_NUMBER_NL;
const testPostalCode = (pattern, postCode) => !!postCode && new RegExp(pattern, 'i').test(postCode);
export const isValidAddress = (postCode, houseNumber, countryId) => {
    switch (countryId) {
        case CountryId.NL:
            return (!!houseNumber &&
                new RegExp(getHouseNumberNLPattern(houseNumber), 'i').test(houseNumber) &&
                testPostalCode(DefaultSettings.POSTAL_CODE_NL, postCode));
        case CountryId.BE:
            return testPostalCode(DefaultSettings.POSTAL_CODE_BE, postCode);
        case CountryId.GB:
        case CountryId.GG:
        case CountryId.JE:
            return testPostalCode(DefaultSettings.POSTAL_CODE_UK, postCode);
        default:
            return true;
    }
};
export const isValidPostCode = (postCode, countryId) => {
    switch (countryId) {
        case CountryId.BE:
            return testPostalCode(DefaultSettings.POSTAL_CODE_BE, postCode);
        case CountryId.FR:
        case CountryId.DE:
            return testPostalCode(DefaultSettings.POSTAL_CODE_DE_FR, postCode);
        case CountryId.NL:
            return testPostalCode(DefaultSettings.POSTAL_CODE_NL, postCode);
        case CountryId.GB:
        case CountryId.GG:
        case CountryId.JE:
            return testPostalCode(DefaultSettings.POSTAL_CODE_UK, postCode);
        default:
            return true;
    }
};
const whiteListProperties = ['idCountry', 'country', 'state', 'customTown'];
export const hasAnyAddressValues = (object, filterKeys) => Object.entries(object)
    .filter(([key]) => !(filterKeys ? [...whiteListProperties, ...filterKeys] : whiteListProperties).includes(key))
    .some(([, fieldValue]) => fieldValue && String(fieldValue).trim() !== '');
export const getAddressValidationSchema = ({ country, t, prefix, isOptional }) => {
    var _a;
    const objectName = (_a = prefix === null || prefix === void 0 ? void 0 : prefix.split('.')[0]) !== null && _a !== void 0 ? _a : '';
    const [addressName, houseNumberName, houseNumberAdditionName, postCodeName, cityName, stateName, finderName] = mapWithAssert([
        AddressFieldName.ADDRESS,
        AddressFieldName.HOUSE_NUMBER,
        AddressFieldName.HOUSE_NUMBER_ADDITION,
        AddressFieldName.POSTCODE,
        AddressFieldName.CITY,
        AddressFieldName.STATE,
        AddressFieldName.FINDER,
    ], name => generateFieldName(name));
    const defaultRequiredMessage = t(ErrorMessage.ACCOUNT_REQUIRED);
    const addIsOptional = (values) => (Object.assign(Object.assign({}, prepareAddressByCountry(values, '')), { isOptional }));
    const addressSchema = () => {
        switch (country === null || country === void 0 ? void 0 : country.toUpperCase()) {
            case 'NL':
                return Yup.object({
                    [postCodeName]: Yup.string()
                        .when('isOptional', checkOptional({ t }))
                        .test(checkIfAnyFieldHasValue({ t }))
                        .conditionalMatches(new RegExp(DefaultSettings.POSTAL_CODE_NL, 'i'), t(ErrorMessage.INVALID_POSTAL_CODE_NL)),
                    [houseNumberName]: Yup.string()
                        .when('isOptional', checkOptional({ t }))
                        .test(checkIfAnyFieldHasValue({ t }))
                        .test('checkNlHouseNumber', t(ErrorMessage.INVALID_HOUSE_NUMBER_NL), houseNumber => {
                        if (!houseNumber)
                            return true;
                        return new RegExp(getHouseNumberNLPattern(houseNumber), 'i').test(houseNumber);
                    }),
                    [addressName]: Yup.string()
                        .when('isOptional', checkOptional({ t }))
                        .test(checkIfAnyFieldHasValue({ t }))
                        .conditionalMatches(new RegExp(DefaultSettings.STREET_NAME_REGEX), t(ErrorMessage.INVALID_STREET)),
                    [cityName]: Yup.string().when('isOptional', checkOptional({ t })).test(checkIfAnyFieldHasValue({ t })),
                }).transform(addIsOptional);
            case 'DE':
            case 'FR':
                return Yup.object({
                    [addressName]: Yup.string()
                        .when('isOptional', checkOptional({ t }))
                        .test(checkIfAnyFieldHasValue({ t }))
                        .conditionalMatches(new RegExp(DefaultSettings.STREET_NAME_REGEX), t(ErrorMessage.INVALID_STREET)),
                    [houseNumberName]: Yup.string().when('isOptional', checkOptional({ t })).test(checkIfAnyFieldHasValue({ t })),
                    [postCodeName]: Yup.string()
                        .when('isOptional', checkOptional({ t }))
                        .test(checkIfAnyFieldHasValue({ t }))
                        .conditionalMatches(new RegExp(DefaultSettings.POSTAL_CODE_DE_FR, 'i'), t(ErrorMessage.INVALID_POSTAL_CODE)),
                    [cityName]: Yup.string().when('isOptional', checkOptional({ t })).test(checkIfAnyFieldHasValue({ t })),
                }).transform(addIsOptional);
            case 'US':
                return Yup.object({
                    [addressName]: Yup.string().when('isOptional', checkOptional({ t })).test(checkIfAnyFieldHasValue({ t })),
                    [postCodeName]: Yup.string().when('isOptional', checkOptional({ t })).test(checkIfAnyFieldHasValue({ t })),
                    [cityName]: Yup.string().when('isOptional', checkOptional({ t })).test(checkIfAnyFieldHasValue({ t })),
                    [stateName]: Yup.string().when('isOptional', checkOptional({ t })).test(checkIfAnyFieldHasValue({ t })),
                }).transform(addIsOptional);
            case 'BE':
            case 'LU':
                return Yup.object({
                    [addressName]: Yup.string()
                        .when('isOptional', checkOptional({ t }))
                        .test(checkIfAnyFieldHasValue({ t }))
                        .conditionalMatches(new RegExp(DefaultSettings.STREET_BE_LU_REGEXP), t(ErrorMessage.INVALID_STREET_BE_LU)),
                    [houseNumberName]: Yup.string()
                        .when('isOptional', checkOptional({ t }))
                        .test(checkIfAnyFieldHasValue({ t }))
                        .conditionalMatches(new RegExp(DefaultSettings.HOUSE_NUMBER_BE_LU_REGEXP), t(ErrorMessage.INVALID_HOUSE_NUMBER_BE_LU)),
                    [houseNumberAdditionName]: Yup.string().conditionalMatches(new RegExp(DefaultSettings.HOUSE_NUMBER_ADDITION_BE_LU_REGEXP), t(ErrorMessage.INVALID_HOUSE_NUMBER_ADDITION_BE_LU)),
                    [postCodeName]: Yup.string()
                        .when('isOptional', checkOptional({ t }))
                        .test(checkIfAnyFieldHasValue({ t }))
                        .conditionalMatches(new RegExp(DefaultSettings.POSTAL_CODE_BE, 'i'), t(ErrorMessage.INVALID_POSTAL_CODE_BE)),
                    [cityName]: Yup.string().when('isOptional', checkOptional({ t })).test(checkIfAnyFieldHasValue({ t })),
                }).transform(addIsOptional);
            case 'GB':
            case 'GG':
            case 'JE':
                return Yup.object({
                    [finderName]: Yup.string()
                        .when([addressName, 'isOptional'], {
                        is: (addressValue, isOptionalValue) => !addressValue && !isOptionalValue,
                        then: () => Yup.string()
                            .required(defaultRequiredMessage)
                            .test('isValidUKAddress', t(ErrorMessage.GENERAL_ADDRESS_BUY), () => false),
                    })
                        .when([addressName, 'isOptional'], {
                        is: (addressValue, isOptionalValue) => !addressValue && isOptionalValue,
                        then: () => Yup.string().test('isValidUKAddress', t(ErrorMessage.GENERAL_ADDRESS_BUY), finderValue => !finderValue),
                    }),
                    [addressName]: Yup.string()
                        .when('isOptional', checkOptional({ t }))
                        .test(checkIfAnyFieldHasValue({ t }))
                        .conditionalMatches(new RegExp(DefaultSettings.STREET_NAME_REGEX), t(ErrorMessage.INVALID_STREET)),
                    [postCodeName]: Yup.string()
                        .when('isOptional', checkOptional({ t }))
                        .test(checkIfAnyFieldHasValue({ t }))
                        .conditionalMatches(new RegExp(DefaultSettings.POSTAL_CODE_UK), t(ErrorMessage.INVALID_POSTAL_CODE_UK)),
                    [cityName]: Yup.string().when('isOptional', checkOptional({ t })).test(checkIfAnyFieldHasValue({ t })),
                }).transform(addIsOptional);
            default:
                return Yup.object({
                    [addressName]: Yup.string().when('isOptional', checkOptional({ t })).test(checkIfAnyFieldHasValue({ t })),
                    [postCodeName]: Yup.string().when('isOptional', checkOptional({ t })).test(checkIfAnyFieldHasValue({ t })),
                    [cityName]: Yup.string().when('isOptional', checkOptional({ t })).test(checkIfAnyFieldHasValue({ t })),
                }).transform(addIsOptional);
        }
    };
    return objectName
        ? Yup.object({
            [objectName]: addressSchema(),
        })
        : addressSchema();
};
export const getAddressSearchValidationSchema = ({ country, t, isOptional }) => {
    const addIsOptional = (values) => ({
        postCode: values.postCode || '',
        houseNumber: values.houseNumber || '',
        isOptional,
    });
    switch (country) {
        case 'NL': {
            return Yup.object({
                [AddressFieldName.POSTCODE]: Yup.string()
                    .when('isOptional', checkOptional({ t }))
                    .test(checkIfAnyFieldHasValue({ t }))
                    .conditionalMatches(new RegExp(DefaultSettings.POSTAL_CODE_NL, 'i'), t(ErrorMessage.INVALID_POSTAL_CODE_NL)),
                [AddressFieldName.HOUSE_NUMBER]: Yup.string()
                    .when('isOptional', checkOptional({ t }))
                    .test(checkIfAnyFieldHasValue({ t }))
                    .test('checkNlHouseNumber', t(ErrorMessage.INVALID_HOUSE_NUMBER_NL), houseNumber => {
                    if (!houseNumber)
                        return true;
                    return new RegExp(getHouseNumberNLPattern(houseNumber), 'i').test(houseNumber);
                }),
            }).transform(addIsOptional);
        }
        default:
            return Yup.object({
                [AddressFieldName.POSTCODE]: Yup.string(),
                [AddressFieldName.HOUSE_NUMBER]: Yup.string(),
            });
    }
};
const checkIfAnyFieldHasValue = ({ t }) => ({
    name: 'checkIfAnyFieldHasValue',
    message: t(ErrorMessage.ACCOUNT_REQUIRED),
    test: (value, context) => {
        const formValues = context.parent || {};
        const anyFieldHasValue = hasAnyAddressValues(formValues, [context.path, 'isOptional']);
        return !(anyFieldHasValue && (!value || value.trim() === ''));
    },
});
const checkOptional = ({ t }) => ({
    is: (val) => val === true,
    then: (schema) => schema.notRequired().nullable(),
    otherwise: (schema) => schema.required(t(ErrorMessage.ACCOUNT_REQUIRED)),
});
export const prepareAddressByCountry = (address, emptyValue) => {
    switch (Number(address.idCountry)) {
        case CountryId.NL:
        case CountryId.BE:
        case CountryId.LU:
        case CountryId.FR:
            return Object.assign(Object.assign(Object.assign({}, address), emptyAddressFields), { postCode: address.postCode || emptyValue, houseNumber: address.houseNumber || emptyValue, houseNumberAddition: address.houseNumberAddition || emptyValue, address: address.address || emptyValue, city: address.city || emptyValue, idCountry: address.idCountry, country: address.country });
        case CountryId.DE:
            return Object.assign(Object.assign(Object.assign({}, address), emptyAddressFields), { postCode: address.postCode || emptyValue, houseNumber: address.houseNumber || emptyValue, address: address.address || emptyValue, city: address.city || emptyValue, idCountry: address.idCountry, country: address.country });
        case CountryId.GB:
        case CountryId.GG:
        case CountryId.JE:
            return Object.assign(Object.assign(Object.assign({}, address), emptyAddressFields), { address: address.address || emptyValue, addressLine2: address.addressLine2 || emptyValue, addressLine3: address.addressLine3 || emptyValue, addressLine4: address.addressLine4 || emptyValue, postCode: address.postCode || emptyValue, city: address.city || emptyValue, county: address.county || emptyValue, idCountry: address.idCountry, country: address.country });
        case CountryId.US:
            return Object.assign(Object.assign(Object.assign({}, address), emptyAddressFields), { address: address.address || emptyValue, addressLine2: address.addressLine2 || emptyValue, postCode: address.postCode || emptyValue, city: address.city || emptyValue, county: address.state || emptyValue, idCountry: address.idCountry, country: address.country });
        default:
            return Object.assign(Object.assign(Object.assign({}, address), emptyAddressFields), { address: address.address || emptyValue, addressLine2: address.addressLine2 || emptyValue, postCode: address.postCode || emptyValue, city: address.city || emptyValue, idCountry: address.idCountry, country: address.country });
    }
};
export const hasPrefilledAddress = (address = {}) => {
    const fieldsName = [
        AddressFieldName.ADDRESS,
        AddressFieldName.ADDRESS_LINE_2,
        AddressFieldName.ADDRESS_LINE_3,
        AddressFieldName.ADDRESS_LINE_4,
        AddressFieldName.POSTCODE,
        AddressFieldName.CITY,
        AddressFieldName.COUNTY,
    ];
    return Object.entries(address)
        .filter(([key]) => fieldsName.includes(key))
        .some(([, value]) => Boolean(value));
};
export const isStoreDeliveryAddress = (address) => {
    return 'storeNumber' in address && !!address.storeNumber;
};
